import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FaCalendarAlt } from 'react-icons/fa';

import {
  useGetContractsVatPercentageDetailsQuery,
  useGetContractsVatPercentageSummaryQuery,
  useLazyGetContractsVatPercentageExportDetailsQuery,
} from '@client/api';
import { Card, Pagination, Typography, useRouteDocumentTitle, exportToExcel } from '@client/toolkit';
import { ReportingPaths } from '../reporting.router';
import { VatPercentageTable } from './components/vat-percentage-table';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { SortDirection } from '../components/expanable-table-wraper';
import { DateRangeFilter } from '../components/date-range-filter';
import { dateRangeOptions } from '../utils/date-range-options';
import { MetricCard } from '../components/metric-card';
import { VatVarianceDisplay } from '../components/vat-variance-display';
import { transformToExcelFormat } from './utils/transformToExcelFormat';

export function ReportingVatPercentageRoute() {
  const { t, i18n } = useTranslation('reporting');
  const { t: tGlobal } = useTranslation('global');
  const [searchParams] = useSearchParams();

  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');

  const initialStartDate = startDateParam
    ? dayjs(startDateParam).toDate()
    : new Date(new Date().getFullYear(), 0, 1);
  const initialEndDate = endDateParam
    ? dayjs(endDateParam).toDate()
    : new Date(new Date().getFullYear(), 11, 31);

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('BuildingName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  const [exporting, setExporting] = useState(false);

  useRouteDocumentTitle(t('vatPercentage.documentTitle'), ReportingPaths.VatPercentage);

  const { data: summaryData, isFetching: isFetchingSummary } = useGetContractsVatPercentageSummaryQuery(
    {
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
    },
    {
      skip: !timeRange.startDate || !timeRange.endDate,
    },
  );

  const { data: detailsData, isFetching: isFetchingDetails } = useGetContractsVatPercentageDetailsQuery(
    {
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      orderBy: `${sortColumn}:${sortDirection}`,
      page: currentPage,
      pageSize: 10,
    },
    {
      skip: !timeRange.startDate || !timeRange.endDate,
    },
  );

  const currentYear = timeRange.startDate.getFullYear();
  const comparisonYear = currentYear - 1;

  const [trigger] = useLazyGetContractsVatPercentageExportDetailsQuery();
  const exportExcel = useCallback(async () => {
    setExporting(true);
    const result = await trigger({
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      pageSize: 0,
    });
    if (result.isSuccess && result.data) {
      exportToExcel(
        transformToExcelFormat(
          result.data,
          selectedRows,
          currentYear,
          comparisonYear,
        ),
        'report-vat-percentage.xlsx',
      );
    }
    setExporting(false);
  }, [selectedRows, timeRange, trigger, currentYear, comparisonYear]);

  return (
    <ReportPageContent
      breadcrumb={tGlobal('navigation.reporting.vatPercentage')}
      menu={
        <DateRangeFilter
          options={[dateRangeOptions().currentVsPreviousYear, dateRangeOptions().custom]}
          onChange={(d) => setTimeRange({ startDate: d[0], endDate: d[1] })}
          shouldDisableDate={(date, startDate, endDate, position) =>
            startDate && position === 'end'
              ? date.isBefore(startDate) || date.isAfter(dayjs(startDate).endOf('year'))
              : false
          }
          initialStartDate={timeRange.startDate}
          initialEndDate={timeRange.endDate}
          onExportExcel={exportExcel}
          exporting={exporting}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <MetricCard title={t('vatPercentage.averageVatYear', { year: currentYear })} isLoading={isFetchingSummary}>
          <Typography variantFontSize="h2">
            {summaryData?.currentPeriodAverageVat != null ? `${summaryData.currentPeriodAverageVat}%` : '-'}
          </Typography>
        </MetricCard>
        <MetricCard title={t('vatPercentage.averageVatYear', { year: comparisonYear })} isLoading={isFetchingSummary}>
          <Typography variantFontSize="h2">
            {summaryData?.comparisonPeriodAverageVat != null ? `${summaryData.comparisonPeriodAverageVat}%` : '-'}
          </Typography>
        </MetricCard>
        <MetricCard title={t('vatPercentage.development')} isLoading={isFetchingSummary}>
          <VatVarianceDisplay variance={summaryData?.variance} size="2xl" />
        </MetricCard>
      </div>
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">
            {t('dateRangeBaseVsComparison', {
              baseStartDate: dayjs(timeRange.startDate).subtract(1, 'year').toDate().toLocaleDateString(i18n.language),
              baseEndDate: dayjs(timeRange.endDate).subtract(1, 'year').toDate().toLocaleDateString(i18n.language),
              comparisonStartDate: timeRange.startDate.toLocaleDateString(i18n.language),
              comparisonEndDate: timeRange.endDate.toLocaleDateString(i18n.language),
            })}
          </Typography>
        </span>
        <Card className="mb-4">
          <VatPercentageTable
            data={detailsData?.items ?? []}
            loading={isFetchingDetails}
            baseYear={currentYear}
            comparisonYear={comparisonYear}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
            onSort={(col, direction) => {
              setSortColumn(col);
              setSortDirection(direction);
              setCurrentPage(0);
            }}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
          <Pagination
            totalCount={detailsData?.total ?? 0}
            currentPage={currentPage}
            setPage={setCurrentPage}
            pageSize={detailsData?.pageSize ?? 10}
            visible
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
